import { useState } from 'react'
import { AlertCircle, Award, Search, RefreshCw, Flame } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Label,
} from '../ui'
import { LoadingTable } from '../ui/loading-states'
import { useTalentReport } from '../../hooks/useReports'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import type { TalentReportFilters, TalentRecord } from '../../types/reports.types'

interface Organization {
  id: string
  name: string
}

interface Department {
  id: string
  name: string
}

export function TalentReport() {
  const [filters, setFilters] = useState<TalentReportFilters>({})
  const [shouldFetch, setShouldFetch] = useState(false)

  const { data: organizations } = useOrganizationsForSelect() as { data: Organization[] | undefined }
  const { data: departments } = useDepartmentsForSelect(filters.organizationId || null) as { data: Department[] | undefined }

  const {
    data: report,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useTalentReport(filters, shouldFetch)

  const handleOrganizationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setFilters((prev) => ({
      ...prev,
      organizationId: value || undefined,
      departmentId: undefined,
    }))
  }

  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setFilters((prev) => ({
      ...prev,
      departmentId: value || undefined,
    }))
  }

  const handleAsOfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFilters((prev) => ({
      ...prev,
      asOf: value || undefined,
    }))
  }

  const handleGenerateReport = () => {
    setShouldFetch(true)
    if (shouldFetch) {
      refetch()
    }
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            {/* Organization select */}
            <div className="space-y-2">
              <Label htmlFor="organization">Организация</Label>
              <select
                id="organization"
                value={filters.organizationId || ''}
                onChange={handleOrganizationChange}
                className="h-10 w-[250px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Все организации</option>
                {organizations?.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Department select */}
            <div className="space-y-2">
              <Label htmlFor="department">Подразделение</Label>
              <select
                id="department"
                value={filters.departmentId || ''}
                onChange={handleDepartmentChange}
                disabled={!filters.organizationId}
                className="h-10 w-[250px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Все подразделения</option>
                {departments?.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* As-of date */}
            <div className="space-y-2">
              <Label htmlFor="asOf">Дата формирования</Label>
              <input
                id="asOf"
                type="date"
                value={filters.asOf || ''}
                onChange={handleAsOfChange}
                className="h-10 w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleGenerateReport}
                disabled={isLoading || isFetching}
                className="gap-2"
              >
                {isLoading || isFetching ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Сформировать отчет
              </Button>
              <Button
                variant="outline"
                onClick={() => setFilters({ organizationId: undefined, departmentId: undefined, asOf: undefined })}
              >
                Сбросить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error state */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>Ошибка загрузки отчета. Попробуйте еще раз.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {shouldFetch && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Талант — новички-отличники
            </CardTitle>
            <CardDescription>
              {report ? (
                <>
                  Найдено талантов: {report.meta.total} · приняты с {report.meta.hiredSince} ·
                  множитель награды ×{report.meta.xpMultiplier}
                </>
              ) : (
                'Загрузка...'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading || isFetching ? (
              <LoadingTable rows={10} />
            ) : report && report.data.length > 0 ? (
              <TalentTable records={report.data} multiplier={report.meta.xpMultiplier} />
            ) : (
              <div className="text-center py-8">
                <Award className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                  Нет подходящих сотрудников
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Среди новичков (≤2 мес) нет тех, кто отработал первый месяц без опозданий.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial state - before report generation */}
      {!shouldFetch && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Award className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Нажмите "Сформировать отчет"
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Отчёт покажет новичков (принятых не более 2 месяцев назад), отработавших
                первый месяц без единого опоздания, с их наградами геймификации.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// Table component
function TalentTable({ records, multiplier }: { records: TalentRecord[]; multiplier: number }) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead>Сотрудник</TableHead>
            <TableHead>Организация</TableHead>
            <TableHead>Подразделение</TableHead>
            <TableHead>Должность</TableHead>
            <TableHead>Дата приёма</TableHead>
            <TableHead className="text-center">Дней без опозданий</TableHead>
            <TableHead className="text-center">Уровень</TableHead>
            <TableHead className="text-center">ХР</TableHead>
            <TableHead className="text-center">Серия</TableHead>
            <TableHead className="text-center">Бейджи</TableHead>
            <TableHead className="text-center">Награда ×{multiplier}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record, index) => (
            <TableRow key={record.employee_id}>
              <TableCell className="font-medium">{index + 1}</TableCell>
              <TableCell className="font-medium">{record.employee_name}</TableCell>
              <TableCell>{record.organization_name || '-'}</TableCell>
              <TableCell>{record.department_name || '-'}</TableCell>
              <TableCell>{record.position_name || '-'}</TableCell>
              <TableCell>{record.hire_date || '-'}</TableCell>
              <TableCell className="text-center">{record.worked_days}</TableCell>
              <TableCell className="text-center">{record.level}</TableCell>
              <TableCell className="text-center">{record.total_xp}</TableCell>
              <TableCell className="text-center">
                {record.current_streak_days > 0 ? (
                  <span className="inline-flex items-center gap-1 text-orange-600">
                    <Flame className="h-4 w-4" />
                    {record.current_streak_days}
                  </span>
                ) : (
                  '0'
                )}
              </TableCell>
              <TableCell className="text-center">{record.badges_count}</TableCell>
              <TableCell className="text-center font-semibold text-primary">
                {record.xp_with_multiplier}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
